// src/lib/components/atoms/ChipListDisplay/ChipListDisplayAtom.tsx

import React from 'react';
import { Tag } from 'primereact/tag';
import './ChipListDisplayAtom.scss';

export interface ChipListDisplayConfig {
  /** Field to use as the primary label */
  labelField: string;
  /** Optional secondary field to display (e.g., date) */
  secondaryField?: string;
  /** Optional icon field */
  iconField?: string;
  /** Date format string (e.g., 'MMM DD, YYYY') */
  dateFormat?: string;
  /** Map field values to PrimeReact severity classes */
  severityMap?: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast'>;
  /** Field to use for determining severity */
  severityField?: string;
  /** Icon map - map field values to icon classes */
  iconMap?: Record<string, string>;
  /** Empty state message */
  emptyMessage?: string;
  /** Custom label template using {{field}} syntax */
  labelTemplate?: string;
}

export interface ChipListDisplayAtomProps {
  /** Array of data objects to display as chips */
  data?: any[];
  /** Configuration for how to display the chips */
  config: ChipListDisplayConfig;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show in compact mode */
  compact?: boolean;
}

/**
 * Generic chip list display component that can render any array of objects as chips
 * with configurable label, icon, and color mappings.
 */
const ChipListDisplayAtom: React.FC<ChipListDisplayAtomProps> = ({
  data = [],
  config,
  className = '',
  compact = false,
}) => {
  const {
    labelField,
    secondaryField,
    iconField,
    dateFormat = 'MMM DD, YYYY',
    severityMap,
    severityField,
    iconMap,
    emptyMessage = 'No items',
    labelTemplate,
  } = config;

  // Helper to safely access nested fields
  const getFieldValue = (obj: any, field: string): any => {
    if (!field || !obj) return undefined;
    return field.split('.').reduce((o, key) => (o != null ? o[key] : undefined), obj);
  };

  // Helper to format date values
  const formatDate = (value: any): string => {
    if (!value) return '';

    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);

      // Simple date formatting based on dateFormat
      const options: Intl.DateTimeFormatOptions = {};

      if (dateFormat.includes('MMM')) {
        options.month = 'short';
      } else if (dateFormat.includes('MM')) {
        options.month = '2-digit';
      }

      if (dateFormat.includes('DD')) {
        options.day = '2-digit';
      } else if (dateFormat.includes('D')) {
        options.day = 'numeric';
      }

      if (dateFormat.includes('YYYY')) {
        options.year = 'numeric';
      } else if (dateFormat.includes('YY')) {
        options.year = '2-digit';
      }

      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return String(value);
    }
  };

  // Helper to build label from template or fields
  const buildLabel = (item: any): string => {
    if (labelTemplate) {
      // Replace {{field}} and {{field|formatter}} syntax
      return labelTemplate.replace(/\{\{([^}|]+)(\|([^}]+))?\}\}/g, (match, field, _, formatter) => {
        const value = getFieldValue(item, field.trim());
        if (formatter === 'formatDate') {
          return formatDate(value);
        }
        return String(value || '');
      });
    }

    // Build from labelField and optional secondaryField
    const primaryValue = getFieldValue(item, labelField);
    if (!secondaryField) {
      return String(primaryValue || '');
    }

    const secondaryValue = getFieldValue(item, secondaryField);
    const formattedSecondary = secondaryField.toLowerCase().includes('date')
      ? formatDate(secondaryValue)
      : String(secondaryValue || '');

    return `${primaryValue || ''} - ${formattedSecondary}`;
  };

  // Helper to get severity for a chip
  const getSeverity = (item: any): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' | undefined => {
    if (!severityField || !severityMap) return undefined;
    const fieldValue = getFieldValue(item, severityField);
    return severityMap[fieldValue] || 'secondary';
  };

  // Helper to get icon for a chip
  const getIcon = (item: any): string | undefined => {
    if (!iconField || !iconMap) return undefined;
    const fieldValue = getFieldValue(item, iconField);
    return iconMap[fieldValue];
  };

  // Show empty state
  if (!data || data.length === 0) {
    return (
      <div className={`chip-list-display-atom chip-list-empty ${className}`}>
        <span className="chip-list-empty-message">{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div className={`chip-list-display-atom ${compact ? 'compact' : ''} ${className}`}>
      {data.map((item, index) => {
        const label = buildLabel(item);
        const severity = getSeverity(item);
        const icon = getIcon(item);

        return (
          <Tag
            key={index}
            value={label}
            severity={severity}
            icon={icon}
            className="chip-list-item"
          />
        );
      })}
    </div>
  );
};

export default ChipListDisplayAtom;
